/* i18n-ignore: eenvoudige pagina voor de fotograaf, alleen NL */
import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ImagePlus, Loader2, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { requestUploadSlots, uploadLinkInfo } from "@/lib/media-upload.functions";
import { uploadRefusal } from "@/lib/media-upload-link";

export const Route = createFileRoute("/upload/$token")({
  ssr: false,
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Foto's doorsturen" },
      { name: "robots", content: "noindex, nofollow" },
      { name: "referrer", content: "no-referrer" },
    ],
  }),
  component: UploadPage,
});

type Result = { name: string; state: "bezig" | "klaar" | "mislukt"; message?: string };
const MEDIA_BUCKET = "blog-images";

function UploadPage() {
  const { token } = Route.useParams();
  const info = useQuery({
    queryKey: ["upload-link", token],
    queryFn: () => uploadLinkInfo({ data: { token } }),
    retry: false,
  });
  const input = useRef<HTMLInputElement>(null);
  const [results, setResults] = useState<Result[]>([]);
  const [busy, setBusy] = useState(false);

  async function send(files: File[]) {
    if (files.length === 0) return;
    setBusy(true);
    const base = results.length;
    setResults((prev) => [
      ...prev,
      ...files.map((f) => ({ name: f.name, state: "bezig" as const })),
    ]);
    const mark = (index: number, patch: Partial<Result>) =>
      setResults((prev) => prev.map((r, i) => (i === base + index ? { ...r, ...patch } : r)));
    const { supabase } = await import("@/integrations/supabase/client");
    // In blokken van tien: de server geeft per bestand een eigen plek.
    for (let start = 0; start < files.length; start += 10) {
      const batch = files.slice(start, start + 10);
      let slots;
      try {
        slots = await requestUploadSlots({
          data: {
            token,
            files: batch.map((f) => ({ name: f.name, size: f.size, type: f.type })),
          },
        });
      } catch (error) {
        batch.forEach((_, i) =>
          mark(start + i, {
            state: "mislukt",
            message: error instanceof Error ? error.message : "Opladen is niet gelukt.",
          }),
        );
        continue;
      }
      for (const [i, file] of batch.entries()) {
        const slot = slots[i];
        if (!slot) {
          mark(start + i, { state: "mislukt", message: "Geen plek meer op deze link." });
        } else if (!slot.ok) {
          mark(start + i, { state: "mislukt", message: slot.error });
        } else {
          const { error } = await supabase.storage
            .from(MEDIA_BUCKET)
            .uploadToSignedUrl(slot.path, slot.uploadToken, file, {
              contentType: file.type || "image/jpeg",
            });
          mark(
            start + i,
            error
              ? { state: "mislukt", message: "Opladen is niet gelukt. Probeer het opnieuw." }
              : { state: "klaar" },
          );
        }
      }
    }
    setBusy(false);
    void info.refetch();
  }

  function pick(list: FileList | null) {
    const files = Array.from(list ?? []);
    const refused = files.filter((f) => uploadRefusal(f));
    const accepted = files.filter((f) => !uploadRefusal(f));
    if (refused.length > 0)
      setResults((prev) => [
        ...prev,
        ...refused.map((f) => ({
          name: f.name,
          state: "mislukt" as const,
          message: uploadRefusal(f) ?? "",
        })),
      ]);
    void send(accepted);
  }

  return (
    <main className="mx-auto min-h-screen w-full max-w-xl px-4 py-10">
      <h1 className="text-2xl font-extrabold tracking-tight">Foto's doorsturen</h1>

      {info.isLoading && <p className="mt-6 text-sm text-muted-foreground">Even kijken…</p>}
      {info.data && !info.data.valid && (
        <p role="alert" className="mt-6 rounded-lg border p-4 text-sm">
          {info.data.reason}
        </p>
      )}
      {info.isError && (
        <p role="alert" className="mt-6 rounded-lg border p-4 text-sm">
          De pagina kon niet geladen worden. Probeer het later opnieuw.
        </p>
      )}

      {info.data?.valid && (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            {info.data.label ? `${info.data.label}. ` : ""}Kies uw foto's op uw gsm of computer. Er
            kunnen nog {info.data.remaining} {info.data.remaining === 1 ? "foto" : "foto's"} bij; de
            link werkt tot{" "}
            {new Date(info.data.expiresAt).toLocaleDateString("nl-BE", {
              day: "numeric",
              month: "long",
            })}
            .
          </p>
          <div
            className="mt-6 flex flex-col items-center gap-3 rounded-xl border-2 border-dashed p-8 text-center"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pick(e.dataTransfer.files);
            }}
          >
            {busy ? (
              <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
            ) : (
              <ImagePlus className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
            )}
            <Button type="button" disabled={busy} onClick={() => input.current?.click()}>
              Foto's kiezen
            </Button>
            <input
              ref={input}
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              aria-label="Foto's kiezen"
              onChange={(e) => {
                pick(e.target.files);
                e.target.value = "";
              }}
            />
            <p className="text-xs text-muted-foreground">
              JPG, PNG, WebP of HEIC, tot 25 MB per foto. Wat u stuurt, wordt eerst nagekeken
              voordat het op de website komt.
            </p>
          </div>
        </>
      )}

      {results.length > 0 && (
        <ul className="mt-6 space-y-2" aria-live="polite">
          {results.map((r, i) => (
            <li key={`${r.name}-${i}`} className="flex items-start gap-2 text-sm">
              {r.state === "bezig" && (
                <Loader2 className="mt-0.5 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {r.state === "klaar" && (
                <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" aria-hidden="true" />
              )}
              {r.state === "mislukt" && (
                <TriangleAlert className="mt-0.5 h-4 w-4 text-destructive" aria-hidden="true" />
              )}
              <span>
                {r.name}
                {r.state === "klaar" && " — ontvangen"}
                {r.message && <span className="block text-xs text-destructive">{r.message}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
